import { useMemo, useState, type ReactElement } from 'react';
import { Button } from '../common/Button';
import { UsageGauge } from '../bot-usage/UsageGauge';
import { WeeklyTrendBars } from '../bot-usage/WeeklyTrendBars';
import { ClaudeCodeIngestPanel } from './ClaudeCodeIngestPanel';
import { useBotUsageAdmin, useClaudeSeatCounts } from '../../hooks/useBotUsage';
import { formatDisplayDateTime } from '../../utils/format';
import { formatUsageSourceSplit, type BotUsageAdminRow, type ClaudeCodeUsage } from '../../services/api/botUsage';
import { LEGACY_EN_NUMBER_LOCALE, formatNumber } from '../../utils/localeFormat';

/**
 * Phase 87 (Plan 87-08 / D-07) — admin "Usage" tab inside the existing Monk
 * Bot Admin page. Self-contained: fetches its own data via
 * `useBotUsageAdmin()`. Matches the sibling bot-admin components' card/table
 * shell and `BotAdminPage.tsx`'s `<thead className="bg-gray-50">` + zebra
 * row styling.
 *
 * The column headers are click-to-sort, and the default order is weekly
 * utilisation descending.
 */

function formatUsd(value: number): string {
  return `$${value.toFixed(2)}`;
}

/** The name the user column shows (and sorts by). */
function displayNameOf(row: BotUsageAdminRow): string {
  return row.displayName ?? row.email ?? row.slackUserId;
}

/**
 * Seat totals across every active user with a granted Claude seat — not just
 * the table's rows — to compare with the Claude admin console's assignment,
 * plus the seat holders who have not connected Monka (they have no row below).
 */
function ClaudeSeatSummary() {
  const { data } = useClaudeSeatCounts();
  if (!data) return null;
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-700">
      <p data-testid="claude-seat-totals">
        <span className="font-semibold text-gray-900">Claude seats:</span> {data.premium} Premium
        {' · '}
        {data.standard} Standard · {data.premium + data.standard} total
      </p>
      {data.notConnected.length > 0 && (
        <div className="mt-3">
          <p className="text-xs font-semibold uppercase text-gray-500">
            Seat but no Monka connection ({data.notConnected.length})
          </p>
          <ul className="mt-2 flex flex-wrap gap-2">
            {data.notConnected.map((holder) => (
              <li
                key={holder.userId}
                title={holder.email}
                className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-2 py-0.5"
              >
                <span>{holder.displayName}</span>
                <SeatBadge seatTier={holder.tier} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function SeatBadge({ seatTier }: { seatTier: string | null }) {
  if (seatTier === 'claude-premium') {
    return (
      <span className="inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700">
        Premium
      </span>
    );
  }
  if (seatTier === 'claude-standard') {
    return (
      <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
        Standard
      </span>
    );
  }
  return <span className="text-sm text-gray-700">—</span>;
}

/**
 * MKS-298 — a person with no Claude Code report at all must read as "nothing
 * arrived", not as `$0`: a zero says "cheap", a gap says "not reporting", and
 * telling them apart is the whole reason the ticket exists.
 */
function NoClaudeCodeData() {
  return (
    <span className="text-xs text-gray-400 italic" title="No Claude Code OpenTelemetry report yet">
      no Claude Code data
    </span>
  );
}

/** A report older than this many days is flagged in the Last report column. */
const STALE_REPORT_DAYS = 7;

function isStaleReport(lastReportAt: string | null, now: number = Date.now()): boolean {
  if (!lastReportAt) return false;
  const parsed = Date.parse(lastReportAt);
  if (Number.isNaN(parsed)) return false;
  return now - parsed > STALE_REPORT_DAYS * 24 * 60 * 60 * 1000;
}

/**
 * What the hover text on the cost/token cells says: Anthropic's own reported
 * figure (the one the cells show) next to our API-rate estimate, the top
 * model, and the main/subagent split.
 */
function claudeCodeTooltip(usage: ClaudeCodeUsage): string {
  return [
    `Reported by Anthropic: $${usage.reportedCostUsd30d.toFixed(2)} / 30d`,
    // MKS-311 — where the 30 days ran: own machines, Monka, Factory™.
    usage.bySource30d ? `${formatUsageSourceSplit(usage.bySource30d)} / 30d` : null,
    `Our estimate (API rates): $${usage.costUsd30d.toFixed(2)} / 30d`,
    usage.topModelId ? `Top model: ${usage.topModelId}` : null,
    `Tokens main/subagent/other: ${formatNumber(usage.tokensMain30d, undefined, LEGACY_EN_NUMBER_LOCALE)} / ${formatNumber(usage.tokensSubagent30d, undefined, LEGACY_EN_NUMBER_LOCALE)} / ${formatNumber(usage.tokensOther30d, undefined, LEGACY_EN_NUMBER_LOCALE)}`,
  ]
    .filter((line): line is string => line !== null)
    .join('\n');
}

function AlertBadge({ alertThresholdFired }: { alertThresholdFired: number | null }) {
  if (alertThresholdFired === 95) {
    return (
      <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
        Urgent
      </span>
    );
  }
  if (alertThresholdFired === 80) {
    return (
      <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
        Warning
      </span>
    );
  }
  return null;
}

/**
 * Sort by descending weekly utilization with null-weekly rows last, so the
 * closest-to-cap user is at the top (Claude's discretion per CONTEXT — the
 * only ordering that serves the phase's purpose). Still the order the table
 * opens in; a header click replaces it.
 */
function sortByWeeklyPctDesc(rows: BotUsageAdminRow[]): BotUsageAdminRow[] {
  return [...rows].sort((a, b) => {
    if (a.weeklyPct === null && b.weeklyPct === null) return 0;
    if (a.weeklyPct === null) return 1;
    if (b.weeklyPct === null) return -1;
    return b.weeklyPct - a.weeklyPct;
  });
}

type SortDirection = 'asc' | 'desc';

interface UsageColumn {
  key: string;
  label: string;
  align: 'left' | 'right';
  /**
   * The sortable value, or `null` when the row has none (never seen, no
   * snapshot, no alert) — a missing value always sorts last, in both
   * directions. Strings compare with `localeCompare`, numbers numerically;
   * `Last seen` is a parsed epoch so it compares as a number.
   */
  value: (row: BotUsageAdminRow) => string | number | null;
  /** Direction the first click on this header uses. */
  initialDirection: SortDirection;
  /** Absent means sortable (the default for every pre-existing column);
   * `false` renders a plain non-interactive header (the trend column). */
  sortable?: boolean;
}

/** `claude-premium` sorts above `claude-standard` above seatless (null —
 * the existing `value()` contract: a missing value always sorts last). */
function seatRank(seatTier: string | null): number | null {
  if (seatTier === 'claude-premium') return 2;
  if (seatTier === 'claude-standard') return 1;
  return null;
}

function toEpoch(iso: string | null): number | null {
  if (!iso) return null;
  const parsed = Date.parse(iso);
  return Number.isNaN(parsed) ? null : parsed;
}

const BASE_COLUMNS: UsageColumn[] = [
  {
    key: 'user',
    label: 'Monksheets user',
    align: 'left',
    value: (row) => displayNameOf(row).toLowerCase(),
    initialDirection: 'asc',
  },
  {
    key: 'session',
    label: 'Session (5h)',
    align: 'left',
    value: (row) => row.sessionPct,
    initialDirection: 'desc',
  },
  {
    key: 'weekly',
    label: 'Weekly (7d)',
    align: 'left',
    value: (row) => row.weeklyPct,
    initialDirection: 'desc',
  },
  {
    key: 'alert',
    label: 'Alert',
    align: 'left',
    value: (row) => row.alertThresholdFired,
    initialDirection: 'desc',
  },
  {
    key: 'source',
    label: 'Source',
    align: 'left',
    value: (row) => row.lastSource?.toLowerCase() ?? null,
    initialDirection: 'asc',
  },
  {
    key: 'lastSeen',
    label: 'Last seen',
    align: 'left',
    value: (row) => toEpoch(row.lastCapturedAt),
    initialDirection: 'desc',
  },
  {
    key: 'costToday',
    label: 'Today $',
    align: 'right',
    value: (row) => row.costUsdToday,
    initialDirection: 'desc',
  },
  {
    key: 'cost30d',
    label: '30d $',
    align: 'right',
    value: (row) => row.costUsd30d,
    initialDirection: 'desc',
  },
  {
    key: 'tokens30d',
    label: '30d tokens',
    align: 'right',
    value: (row) => row.tokens30d,
    initialDirection: 'desc',
  },
];

/**
 * MKS-298: the three cost/token columns are sourced from the OpenTelemetry
 * export instead of the Monka-only turn table, and a "Last report" column
 * joins them. The costs are Anthropic's own reported figures; our API-rate
 * estimate stays in the tooltip. Only the `value` accessors change here — the labels change too,
 * so an admin can see which number they are looking at. `null` keeps its
 * existing meaning (sorts last), which is exactly right for a person who has
 * never reported.
 */
function applyClaudeCodeColumns(columns: UsageColumn[]): UsageColumn[] {
  const next = columns.map((column) => {
    if (column.key === 'costToday') {
      return {
        ...column,
        label: 'Today $',
        value: (row: BotUsageAdminRow) => row.claudeCode?.reportedCostUsdToday ?? null,
      };
    }
    if (column.key === 'cost30d') {
      return {
        ...column,
        label: '30d $',
        value: (row: BotUsageAdminRow) => row.claudeCode?.reportedCostUsd30d ?? null,
      };
    }
    if (column.key === 'tokens30d') {
      return {
        ...column,
        label: '30d tokens',
        value: (row: BotUsageAdminRow) => row.claudeCode?.tokens30d ?? null,
      };
    }
    return column;
  });

  const tokensIndex = next.findIndex((c) => c.key === 'tokens30d');
  next.splice(tokensIndex + 1, 0, {
    key: 'lastReport',
    label: 'Last report',
    align: 'left',
    initialDirection: 'desc',
    value: (row) => toEpoch(row.claudeCode?.lastReportAt ?? null),
  });

  return next;
}

/**
 * Splice a `seat` column right after `user` and a `trend` column right after
 * `weekly`. Splice by locating the index via `findIndex` rather than a
 * hardcoded position, so a later column reorder cannot silently misplace
 * them.
 *
 * MKS-298's transformation is applied last, so it sees the final column list.
 */
function buildColumns(): UsageColumn[] {
  const columns = [...BASE_COLUMNS];

  const weeklyIndex = columns.findIndex((c) => c.key === 'weekly');
  columns.splice(weeklyIndex + 1, 0, {
    key: 'trend',
    label: '10-week trend',
    align: 'left',
    sortable: false,
    initialDirection: 'desc',
    value: () => null,
  });

  const userIndex = columns.findIndex((c) => c.key === 'user');
  columns.splice(userIndex + 1, 0, {
    key: 'seat',
    label: 'Seat',
    align: 'left',
    initialDirection: 'desc',
    value: (row) => seatRank(row.seatTier),
  });

  return applyClaudeCodeColumns(columns);
}

/** The column list never varies at runtime, so it is built once. */
const COLUMNS: UsageColumn[] = buildColumns();

interface UsageSort {
  key: string;
  direction: SortDirection;
}

/**
 * Sort by one column. Rows without a value for that column go last in both
 * directions — the direction only orders the rows that do have one. The sort
 * is stable, so ties keep the server's order (connectedAt desc).
 */
function sortRows(
  rows: BotUsageAdminRow[],
  sort: UsageSort,
  columns: UsageColumn[],
): BotUsageAdminRow[] {
  const column = columns.find((c) => c.key === sort.key);
  if (!column || column.sortable === false) return rows;
  const sign = sort.direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const left = column.value(a);
    const right = column.value(b);
    if (left === null && right === null) return 0;
    if (left === null) return 1;
    if (right === null) return -1;
    const compared =
      typeof left === 'string' && typeof right === 'string'
        ? left.localeCompare(right)
        : Number(left) - Number(right);
    return sign * compared;
  });
}

function SortableHeader({
  column,
  sort,
  onSort,
}: {
  column: UsageColumn;
  sort: UsageSort | null;
  onSort: (key: string) => void;
}) {
  const direction = sort && sort.key === column.key ? sort.direction : null;
  return (
    <th
      className={`px-4 py-3 text-xs font-semibold text-gray-500 uppercase ${
        column.align === 'right' ? 'text-right' : 'text-left'
      }`}
      aria-sort={direction === null ? 'none' : direction === 'asc' ? 'ascending' : 'descending'}
    >
      <button
        type="button"
        onClick={() => onSort(column.key)}
        className={`inline-flex items-center gap-1 uppercase hover:text-gray-700 ${
          column.align === 'right' ? 'flex-row-reverse' : ''
        }`}
      >
        {column.label}
        {/* An inactive header shows a faint ↕ so it reads as clickable. */}
        <span aria-hidden="true" className={direction === null ? 'text-gray-300' : undefined}>
          {direction === null ? '↕' : direction === 'asc' ? '↑' : '↓'}
        </span>
      </button>
    </th>
  );
}

export function UsageTab() {
  const { data: rows, isLoading, error, refetch } = useBotUsageAdmin();
  const [sort, setSort] = useState<UsageSort | null>(null);

  const sortedRows = useMemo(() => {
    const list = rows ?? [];
    // Until a header is clicked the table keeps its default weekly-desc order.
    if (sort === null) return sortByWeeklyPctDesc(list);
    return sortRows(list, sort, COLUMNS);
  }, [rows, sort]);

  const toggleSort = (key: string) => {
    setSort((prev) => {
      if (prev && prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      const column = COLUMNS.find((c) => c.key === key);
      return { key, direction: column?.initialDirection ?? 'asc' };
    });
  };

  /**
   * MKS-298: the ingest-token / unattributed panel belongs above the table in
   * every branch — an admin needs it precisely when nothing is reporting yet,
   * which is also when the table is empty or still loading.
   */
  const withPanel = (content: ReactElement): ReactElement => (
    <div className="space-y-6">
      <ClaudeCodeIngestPanel />
      <ClaudeSeatSummary />
      {content}
    </div>
  );

  if (isLoading) {
    return withPanel(
      <div className="py-12 text-center">
        <p className="text-sm text-gray-500">Loading...</p>
      </div>,
    );
  }

  if (error) {
    return withPanel(
      <div className="py-12 text-center">
        <p className="text-sm font-semibold text-red-600">Failed to load usage data.</p>
        <p className="text-sm text-gray-500 mt-1">{(error as Error).message}</p>
        <Button variant="secondary" size="sm" onClick={() => refetch()} className="mt-4">
          Try again
        </Button>
      </div>,
    );
  }

  if (sortedRows.length === 0) {
    return withPanel(
      <div className="py-12 text-center">
        <p className="text-sm text-gray-500">No bot connections yet.</p>
      </div>,
    );
  }

  return withPanel(
    <div className="overflow-auto rounded-lg border border-gray-200 bg-white">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {COLUMNS.map((column) =>
              column.sortable === false ? (
                <th
                  key={column.key}
                  className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase text-left"
                >
                  {column.label}
                </th>
              ) : (
                <SortableHeader key={column.key} column={column} sort={sort} onSort={toggleSort} />
              ),
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {sortedRows.map((row, idx) => {
            const displayName = displayNameOf(row);
            return (
              <tr key={row.connectionId} className={idx % 2 === 1 ? 'bg-gray-50' : 'bg-white'}>
                <td className="px-4 py-2 text-sm whitespace-nowrap">
                  <div className="flex flex-col">
                    <span className="font-medium text-gray-900 flex items-center gap-2">
                      {displayName}
                      {row.isRobot && (
                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                          Robot
                        </span>
                      )}
                    </span>
                    <span className="text-xs text-gray-400">{row.email ?? '-'}</span>
                  </div>
                </td>
                <td className="px-4 py-2 text-sm whitespace-nowrap">
                  <SeatBadge seatTier={row.seatTier} />
                </td>
                <td className="px-4 py-2 text-sm min-w-[10rem]">
                  <UsageGauge
                    pct={row.sessionPct}
                    resetAt={row.sessionResetAt}
                    label="Session"
                    ariaLabel={`Session usage for ${displayName}`}
                  />
                </td>
                <td className="px-4 py-2 text-sm min-w-[10rem]">
                  <UsageGauge
                    pct={row.weeklyPct}
                    resetAt={row.weeklyResetAt}
                    label="Weekly"
                    ariaLabel={`Weekly usage for ${displayName}`}
                  />
                  {row.weeklyWindowKey && row.weeklyWindowKey !== 'seven_day' && (
                    <span className="text-xs text-gray-400">{row.weeklyWindowKey}</span>
                  )}
                </td>
                <td className="px-4 py-2 text-sm">
                  <WeeklyTrendBars
                    points={row.weeklyTrend ?? []}
                    ariaLabel={`10-week usage trend for ${displayName}`}
                  />
                </td>
                <td className="px-4 py-2 text-sm whitespace-nowrap">
                  <AlertBadge alertThresholdFired={row.alertThresholdFired} />
                </td>
                <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                  {row.lastSource ?? '—'}
                </td>
                <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                  {formatDisplayDateTime(row.lastCapturedAt)}
                </td>
                <td
                  className="px-4 py-2 text-sm text-right text-gray-700 whitespace-nowrap"
                  title={row.claudeCode ? claudeCodeTooltip(row.claudeCode) : undefined}
                >
                  {row.claudeCode ? formatUsd(row.claudeCode.reportedCostUsdToday) : <NoClaudeCodeData />}
                </td>
                <td
                  className="px-4 py-2 text-sm text-right text-gray-700 whitespace-nowrap"
                  title={row.claudeCode ? claudeCodeTooltip(row.claudeCode) : undefined}
                >
                  {row.claudeCode ? formatUsd(row.claudeCode.reportedCostUsd30d) : <NoClaudeCodeData />}
                </td>
                <td
                  className="px-4 py-2 text-sm text-right text-gray-700 whitespace-nowrap"
                  title={row.claudeCode ? claudeCodeTooltip(row.claudeCode) : undefined}
                >
                  {row.claudeCode ? (
                    formatNumber(row.claudeCode.tokens30d, undefined, LEGACY_EN_NUMBER_LOCALE)
                  ) : (
                    <NoClaudeCodeData />
                  )}
                </td>
                <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                  {row.claudeCode?.lastReportAt ? (
                    <span className="flex items-center gap-2">
                      {formatDisplayDateTime(row.claudeCode.lastReportAt)}
                      {isStaleReport(row.claudeCode.lastReportAt) && (
                        <span
                          className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700"
                          title={`No Claude Code report for over ${STALE_REPORT_DAYS} days`}
                        >
                          Stale
                        </span>
                      )}
                    </span>
                  ) : (
                    <NoClaudeCodeData />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>,
  );
}
